import React, { useEffect, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import MessageTemplateModal from "../../Common/MessageTemplateModal";
import { postData } from "../../utility/Utility";
import CustomLoader from "../../Components/CustomLoader/CustomLoader";

function HolidayCalendar() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedFestival, setSelectedFestival] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch holidays from API
  const fetchHolidays = async () => {
    try {
      setLoading(true);
      const response = await postData("api/event-templates/holiday", {});

      if (response && response.data) {
        setHolidays(response.data);
      } else {
        setHolidays([]);
      }
    } catch (err) {
      setError("Failed to fetch holidays");
      setHolidays([]);
      console.error("Error fetching holidays:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHolidays();
  }, []);

  // Check if date is current or future
  const isCurrentOrFutureDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);

    return checkDate >= today;
  };

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // Format date for display
  const formatDateForDisplay = (date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long'
    });
  };

  // Get all festivals for a date
  const getAllFestivalsForDate = (date) => {
    if (!holidays || holidays.length === 0) return [];

    const dateString = date.toISOString().split('T')[0];
    return holidays.filter(holiday => holiday.date === dateString);
  };

  // Custom tile content to show festivals and holidays
  const tileContent = ({ date, view }) => {
    if (view === 'month') {
      const festivals = getAllFestivalsForDate(date);
      const isFutureDate = isCurrentOrFutureDate(date);

      if (festivals.length === 0) return null;

      return (
        <div className="d-flex flex-column align-items-center justify-content-center h-100 w-100">
          {/* Festival name with better overflow handling */}
          {festivals.length > 0 && (
            <div
              className={`text-center fw-bold w-100 px-1 ${festivals[0].type.toLowerCase().includes('gazetted') || festivals[0].type.toLowerCase().includes('restricted') ? 'text-info' : 'text-success'} ${isFutureDate ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
              title={festivals.map(f => f.name).join(', ')}
              style={{
                fontSize: '0.7rem',
                lineHeight: '1.1',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
                display: 'block'
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (isFutureDate) {
                  handleFestivalClick(date, festivals[0]);
                }
              }}
            >
              {festivals[0].name}
              {festivals.length > 1 && (
                <span
                  className="badge bg-secondary ms-1"
                  style={{
                    fontSize: '0.5rem',
                    padding: '1px 3px'
                  }}
                >
                  +{festivals.length - 1}
                </span>
              )}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  // Handle festival click
  const handleFestivalClick = (date, festival) => {
    if (!isCurrentOrFutureDate(date)) {
      return;
    }

    setSelectedDate(date);
    setSelectedFestival({
      name: festival.name,
      type: festival.type.toLowerCase().includes('gazetted') ? 'holiday' :
        festival.type.toLowerCase().includes('restricted') ? 'holiday' : 'festival',
      originalType: festival.type,
      description: festival.description
    });
    setShowMessageModal(true);
  };

  // Handle date click on calendar - ONLY FOR FUTURE DATES
  const handleDateClick = (date) => {
    // Only allow clicks on current or future dates
    if (!isCurrentOrFutureDate(date)) {
      return;
    }

    const festivals = getAllFestivalsForDate(date);

    // Set selected date and festival (even if no festival exists)
    setSelectedDate(date);

    if (festivals.length > 0) {
      setSelectedFestival({
        name: festivals[0].name,
        type: festivals[0].type.toLowerCase().includes('gazetted') ? 'holiday' :
          festivals[0].type.toLowerCase().includes('restricted') ? 'holiday' : 'festival',
        originalType: festivals[0].type,
        description: festivals[0].description
      });
    } else {
      // For dates without festivals, still allow template creation
      setSelectedFestival({
        name: "Custom Date",
        type: "custom",
        originalType: "Custom Date",
        description: "Create a custom message template for this date"
      });
    }

    setShowMessageModal(true);
  };

  // Custom class for tiles - DISABLE PAST DATES
  const tileClassName = ({ date, view }) => {
    if (view === 'month') {
      const festival = getAllFestivalsForDate(date).length > 0;
      const today = isToday(date);
      const isFutureDate = isCurrentOrFutureDate(date);

      let className = 'border-0 ';

      if (festival) {
        className += 'bg-light ';
      }
      if (today) {
        className += 'bg-warning text-dark ';
      }
      if (!isFutureDate) {
        className += 'past-date disabled-date ';
      }

      return className.trim();
    }
    return '';
  };

  // Custom navigation label
  const navigationLabel = ({ date }) => {
    return formatDateForDisplay(date);
  };

  // Add custom CSS for disabled dates
  const calendarStyles = `
    .react-calendar__tile.past-date {
      background-color: #f8f9fa !important;
      color: #6c757d !important;
      cursor: not-allowed !important;
    }
    
    .react-calendar__tile.past-date:hover {
      background-color: #f8f9fa !important;
    }
    
    .react-calendar__tile.disabled-date {
      pointer-events: none !important;
    }
    
    .react-calendar__tile:not(.past-date) {
      cursor: pointer !important;
    }
    
    /* Better text overflow handling */
    .react-calendar__tile > div > div {
      max-width: 100% !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
    }
  `;



  if (error) {
    return (
      <div className="content-page">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 col-lg-12">
              <div className="card shadow">
                <div className="card-body text-center py-5">
                  <div className="text-danger mb-3">
                    <i className="mdi mdi-alert-circle-outline" style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h5 className="text-danger">Failed to load holidays</h5>
                  <p className="text-muted">{error}</p>
                  <button
                    className="btn btn-primary mt-3"
                    onClick={fetchHolidays}
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="content-page">

    <CustomLoader loading={loading} />
      <style>{calendarStyles}</style>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-12">
            <div className="card shadow">
              <div className="card-header bg-primary-1 text-white text-center">
                <h4 className="card-title mb-1">
                  <i className="mdi mdi-calendar-text me-2"></i>
                  Holiday & Festival Calendar
                </h4>
                <p className="card-text text-white-50 mb-0">
                  {holidays.length > 0
                    ? `Showing ${holidays.length} holidays and festivals. Click on any current or future date to create greeting message templates.`
                    : 'Click on any current or future date to create greeting message templates.'
                  }
                </p>
              </div>

              <div className="card-body">
                <Calendar
                  onClickDay={handleDateClick}
                  tileContent={tileContent}
                  tileClassName={tileClassName}
                  navigationLabel={navigationLabel}
                  className="w-100"
                  showNeighboringMonth={false}
                  minDetail="month"
                  maxDetail="month"
                />
              </div>

              {/* Enhanced Legend */}
              <div className="card-footer">
                <div className="row text-center">
                  <div className="col-12 col-md-3 mb-2 mb-md-0">
                    <span className="badge bg-success me-2">Festival</span>
                    <small>Festival/Observance</small>
                  </div>
                  <div className="col-12 col-md-3 mb-2 mb-md-0">
                    <span className="badge bg-info me-2">Holiday</span>
                    <small>Gazetted/Restricted</small>
                  </div>
                  <div className="col-12 col-md-3 mb-2 mb-md-0">
                    <span className="badge bg-warning text-dark me-2">Today</span>
                    <small>Today</small>
                  </div>
                  <div className="col-12 col-md-3">
                    <span className="badge bg-secondary me-2">Past</span>
                    <small>Past Dates (Disabled)</small>
                  </div>
                </div>
                <div className="row text-center mt-2">
                  <div className="col-12">
                    <small className="text-muted">
                      💡 <strong>Tip:</strong> You can click on any current or future date (with or without festivals) to create message templates
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Message Template Modal */}
        <MessageTemplateModal
          isOpen={showMessageModal}
          onClose={() => {
            setShowMessageModal(false);
            setSelectedFestival(null);
            setSelectedDate(null);
          }}
          selectedDate={selectedDate}
          festival={selectedFestival}
        />
      </div>
    </div>
  );
}

export default HolidayCalendar;